<script setup lang="ts">
/**
 * 授權清單（`/licenses`；`meta.roles: [admin, superadmin]`——本頁沒有 operator，客戶下拉不必 gate）。
 *
 * 過濾器兩層（同 `DeploymentsView`／`JobsView`）：**客戶／代管主機下拉送伺服器**、
 * **狀態桶留前端**（計數要一次算齊）。
 *
 * 本頁**刻意不做型態（代管／自部署）桶**——型態的主維度在部署頁；這裡有主機下拉
 * ＋「主機」欄的代管 chip 就夠了，多一排籤只是噪音（主人裁示）。
 */
import { computed, onMounted, ref, watch } from 'vue'

import FilterClearButton from '@/components/FilterClearButton.vue'
import LicenseDetailModal from '@/components/LicenseDetailModal.vue'
import QuotaSummary from '@/components/QuotaSummary.vue'
import ReissueModal from '@/components/ReissueModal.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as hosting from '@/api/hosting'
import type { ManagedHost } from '@/api/hosting'
import * as ls from '@/api/ls'
import type { License, LicenseAddon } from '@/api/ls'
import {
  LICENSE_WARN_DAYS,
  daysUntilExpiry,
  effectiveStatus,
  fmtModulesZh,
  isExpiringSoon,
  modeLabel,
  statusLabel,
} from '@/lib/modules'
import { issuedViaHint, issuedViaLabel } from '@/lib/licenseLabels'
import { ADDON_FILENAME, LICENSE_FILENAME, copyText, saveTextFile } from '@/lib/download'
import { formatRemaining, isShortTerm } from '@/lib/terms'
import { buildQuotaRows, planLabel } from '@/lib/quotas'
import { ROLE_LABELS } from '@/lib/roles'
import { fmtSeen, staleLevel } from '@/lib/staleness'
import { customerOptionLabel } from '@/lib/customerLabel'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()
const auth = useAuthStore()

// --- 過濾器：使用中／等待啟用／到期臨近／已失效／全部 ---
//
// DB 只有 active/revoked/superseded；「等待啟用」（已簽發未取回）與「已到期」由
// effectiveStatus 推導。
//
// ★★ 「到期臨近」**刻意與「使用中」重疊**——它不是一種狀態，是一張待辦清單
//    （距到期 ≤LICENSE_WARN_DAYS 天且仍有效）。所以：
//    ① `bucketOf()` 維持三態、**不要**把 expiring_soon 塞進去；
//    ② 它走獨立 predicate `isExpiringSoon()`，counts 也獨立算；
//    ③ **各籤計數加總 ≠ 總數，這不是 bug**，別「順手修正」成互斥（那會讓
//       「使用中」漏掉快到期的那些，而那正是最該看到的一批）。
type Bucket = 'active' | 'pending_activation' | 'expiring_soon' | 'expired' | 'all'
const FILTERS: { key: Bucket; label: string; hint?: string }[] = [
  { key: 'active', label: '使用中' },
  { key: 'pending_activation', label: '等待啟用' },
  {
    key: 'expiring_soon',
    label: '到期臨近',
    hint: `距到期 ${LICENSE_WARN_DAYS} 天內且仍有效——與「使用中」刻意重疊（這是待辦清單，不是狀態），所以各籤加總會大於總數。`,
  },
  { key: 'expired', label: '已失效' },
  { key: 'all', label: '全部' },
]

// --- 伺服器端過濾（下拉）---
// 批93：產品也是**伺服器端**過濾（`GET /licenses?product=`）——與客戶／主機同一個原則
// （下拉送伺服器、桶留前端），不做前端桶。
// 批186：下拉與狀態籤記在 `lib/persistedFilters`（重新整理、切頁回來都還在）。
const pf = usePersistedFilters(
  'licenses',
  { custFilter: '', hostFilter: '', productFilter: '', resFilter: '', filter: 'active' as Bucket },
  { oneOf: { filter: FILTERS.map((f) => f.key) } },
)
/** `resFilter`＝批107 代理商過濾，語意是**簽發當下的快照**（列上那顆膠囊就是同一個值）。 */
const { custFilter, hostFilter, productFilter, resFilter, filter } = pf.refs
const { dirty: filtersDirty } = pf
const hosts = ref<ManagedHost[]>([])

/** 下拉過濾（客戶／主機／產品／代理商）——計數與清單都吃這一組。 */
function baseFilter() {
  return {
    customerId: custFilter.value || undefined,
    hostId: hostFilter.value || undefined,
    product: productFilter.value || undefined,
    resellerId: resFilter.value || undefined,
  }
}
/**
 * 重載清單（帶目前的籤）＋刷新計數。
 *
 * ★ 批121：籤（`filter`）現在是**伺服器端過濾**，所以換籤要重載，不是在前端 filter。
 *   計數另外一支端點算全表——分頁天生看不到全貌。兩者平行發，計數慢一點也不擋清單。
 */
function reload() {
  const f = baseFilter()
  return Promise.all([
    store.loadLicenses({ ...f, bucket: filter.value === 'all' ? undefined : filter.value }),
    store.loadLicenseCounts(f),
  ])
}

/**
 * keyset 分頁（批93-D §2.10）：後端一頁 50 筆，`store.licensesNextCursor` 非 null＝還有。
 *
 * ★★ 批121：狀態桶（生效中／等待啟用／到期臨近／已失效）**下推到伺服器**了。
 *    在那之前它們是前端算的，於是①籤上的數字只算「已載入的那幾頁」（55 張的站顯示
 *    「全部 50」）②「載入更多」載回的是下一頁原始資料、在某個籤上常常一列都沒增加，
 *    看起來像按了沒反應。現在換籤＝重新查詢，計數走 `GET /licenses/counts`（全表）。
 */
const loadingMore = ref(false)
async function loadMore() {
  if (!store.licensesNextCursor || loadingMore.value) return
  loadingMore.value = true
  try {
    await store.loadMoreLicenses()
  } catch (e) {
    await fail(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

onMounted(async () => {
  await Promise.all([
    store.ensureCustomers(),
    store.ensureProducts().catch(() => {}),
    // 批107：代理商下拉（`ensureResellers` 自己吞錯——拿不到就只是下拉少幾項）。
    store.ensureResellers(),
    reload(),
  ])
  void hosting
    .listManagedHosts()
    .then((rows) => {
      hosts.value = rows
    })
    .catch(() => {})
})

async function fail(e: unknown, fallback: string) {
  const detail = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  const msg = detail == null ? fallback : typeof detail === 'string' ? detail : JSON.stringify(detail)
  await dialogs.notify({ title: '操作失敗', message: msg })
}

function fmt(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—'
}

/**
 * 籤上的數字＝**伺服器算的全表筆數**（批121）。
 *
 * ★★ 以前是把 `store.licenses`（＝已載入的那幾頁）逐列分桶累加，於是資料一超過一頁
 *    就開始少算：ls-dev 實測 55 張、籤卻寫「全部 50」，而第 51 張之後若有生效中的證
 *    完全看不到。分頁天生看不到全貌，計數只能問伺服器。
 * ★ 還沒拿到（首次載入中／計數端點失敗）時退回 `store.licenses.length`——寧可顯示
 *   「至少這麼多」也不要空白跳動。
 */
const counts = computed<Record<Bucket, number>>(() => {
  const c = store.licenseCounts
  if (!c) {
    const n = store.licenses.length
    return { active: n, pending_activation: n, expiring_soon: n, expired: n, all: n }
  }
  return {
    active: c.active,
    pending_activation: c.pending_activation,
    expiring_soon: c.expiring_soon,
    expired: c.expired,
    all: c.all,
  }
})
// ★ 批121：`filter`（狀態籤）也進 watch——它現在是**伺服器端**過濾，換籤要重載。
watch([custFilter, hostFilter, productFilter, resFilter, filter], () => void reload())

// 顯示用衍生資料一列算一次（模組 chip／配額列／有效狀態），template 不重複呼叫。
// ★ 批121：**不再前端過濾**——`store.licenses` 已經是伺服器依 `bucket` 篩過的那一頁。
//   留著 `.filter(inBucket)` 會在「到期臨近」這種跨日邊界的籤上把伺服器給的列再篩掉一次
//   （兩邊的 now 差幾毫秒就差一列），而且「載入更多」載回來的列會被吃掉、看起來沒反應。
const rows = computed(() =>
  store.licenses
    .map((lic) => ({
      lic,
      status: effectiveStatus(lic),
      // 批93：模組／配額的中文名要看**這張證所屬產品**的憑證設計（不同產品鍵不同）。
      // ★★ 批93-B：值一律取 **effective**（主證＋生效加購）。只看主證的話，
      //   「主證 300 篇＋加購到 20000 篇」會顯示成 `1,918 / 300` 紅字＝假警報。
      //   後端無加購時 effective 逐位元等於主證值，所以 `??` 只是舊回應的相容退路。
      modules: fmtModulesZh(
        store.productByKey(lic.product),
        lic.effective_modules ?? lic.modules,
      ),
      // 批114：摘要欄（`<QuotaSummary>`）與〈授權內容〉的計數共用同一份有效值。
      quotas: lic.effective_quotas === undefined ? lic.quotas : lic.effective_quotas,
      quotaRows: buildQuotaRows(
        store.productByKey(lic.product),
        lic.effective_quotas === undefined ? lic.quotas : lic.effective_quotas,
        lic.usage ?? null,
      ),
      // 「尚未回報」只對還有效的證有意義（已失效的證本來就不會再回報）
      awaitingReport: !lic.usage && lic.status === 'active',
      // 到期倒數：null＝無到期日（等待啟用）；負數＝已過期
      daysLeft: daysUntilExpiry(lic.expires_at),
      expiringSoon: isExpiringSoon(lic),
    })),
)

/**
 * 這張證還沒到生效時間嗎（批110／LS #5496）。
 *
 * ★ 站台端**不會**因此進唯讀：CS 收到未來生效的證時會先收著、繼續用手上那張
 *   （`services/license._should_defer`）。所以這個徽章的語意是「已排程」，不是「壞了」。
 */
function notYet(lic: License): boolean {
  return !!lic.not_before && new Date(lic.not_before).getTime() > Date.now()
}

const renewingId = ref<string | null>(null)
const renewDate = ref('')
/** 開續約輸入列：日期預填「今天＋一年」（主人裁示批63；仍可改）。 */
function startRenew(lic: License) {
  const d = new Date()
  d.setFullYear(d.getFullYear() + 1)
  renewDate.value = d.toISOString().slice(0, 10)
  renewingId.value = lic.license_id
}
async function revoke(lic: License) {
  if (!(await dialogs.confirm({ message: `撤銷授權 ${lic.license_id.slice(0, 8)}？此後該部署會轉唯讀。`, danger: true, confirmText: '撤銷' }))) return
  try {
    await ls.revokeLicense(lic.license_id)
    await reload()
  } catch (e) {
    await fail(e, '撤銷失敗')
  }
}
async function renew(lic: License) {
  if (!renewDate.value) return
  try {
    await ls.renewLicense(lic.license_id, renewDate.value)
    renewingId.value = null
    renewDate.value = ''
    await reload()
  } catch (e) {
    await fail(e, '續約失敗')
  }
}
async function remove(lic: License) {
  // 批93-B：刪主證**不影響**加購（子證綁主機不綁證），但操作者該知道這台還掛著幾張
  //   ——不寫的話「刪掉這張證會不會把客戶的加購一起刪掉」只能靠猜。
  const addonHint = lic.addon_count
    ? `
這台主機另有 ${lic.addon_count} 張加購子憑證——它們綁的是主機不是這張證，不會受影響。`
    : ''
  if (
    !(await dialogs.confirm({
      message: `永久刪除授權 ${lic.license_id.slice(0, 8)}？此操作無法復原。${addonHint}`,
      danger: true,
      confirmText: '永久刪除',
    }))
  )
    return
  try {
    await ls.deleteLicense(lic.license_id)
    await reload()
  } catch (e) {
    await fail(e, '刪除失敗')
  }
}

// --- 模組加購子憑證（批93-B）------------------------------------------------
//
// 展開一列才去撈那張證所在**主機**的加購（清單端點只帶 `addon_count` 徽章——一次撈全部
// 加購等於在授權清單上多一趟 N+1，而多數人不會展開任何一列）。
/** 批110：詳情 modal 的目標（null＝關閉）。 */
const detailFor = ref<License | null>(null)
/** 子證資料是「開過哪一張」的快取鍵——同一張再開不重撈。 */
const addonsOpen = ref<string | null>(null)
const addonRows = ref<LicenseAddon[]>([])
const addonsBusy = ref(false)


/**
 * 開〈授權內容〉詳情（批110）。
 *
 * 子證**仍然是點開才撈**（清單只帶 `addon_count`）——預載進清單等於每頁多打 N 次端點，
 * 而多數時候沒有人要看。同一張證再開一次不重撈（`addonsOpen` 就是那個快取鍵）。
 */
async function openDetail(lic: License) {
  detailFor.value = lic
  if (addonsOpen.value === lic.license_id) return
  addonsOpen.value = lic.license_id
  addonRows.value = []
  if (!lic.addon_count) return
  addonsBusy.value = true
  try {
    addonRows.value = await ls.listLicenseAddons(lic.license_id)
  } catch (e) {
    await fail(e, '讀取加購失敗')
  } finally {
    addonsBusy.value = false
  }
}

/** 子證有變動（續期／撤銷）之後重讀這張證的子證與清單。 */
async function refreshAddons(lic: License) {
  addonRows.value = await ls.listLicenseAddons(lic.license_id)
  await reload()
}

async function revokeAddon(lic: License, a: LicenseAddon) {
  if (
    !(await dialogs.confirm({
      title: '撤銷加購',
      message: `撤銷「${a.label}」？下一次心跳送達後，客戶站台的這個加購就會失效。`,
      danger: true,
      confirmText: '撤銷',
    }))
  )
    return
  try {
    const res = await ls.revokeAddon(a.addon_id)
    await refreshAddons(lic)
    if (res.offline_warning) {
      await dialogs.notify({ title: '已撤銷，但請注意', message: res.offline_warning })
    }
  } catch (e) {
    await fail(e, '撤銷加購失敗')
  }
}

async function renewAddon(lic: License, a: LicenseAddon) {
  if (
    !(await dialogs.confirm({
      title: '續期加購',
      message: `為「${a.label}」再續一期（${a.term_days ?? '沿用原'} 天）？會簽出一張新的子憑證取代它。`,
      confirmText: '續期',
    }))
  )
    return
  try {
    await ls.renewAddon(a.addon_id)
    await refreshAddons(lic)
  } catch (e) {
    await fail(e, '續期失敗')
  }
}

// --- 換發新證（批108）-------------------------------------------------------
//
// 以基準證綁定的**機器**為錨點重簽；舊證標「已被取代」。內容偏離套裝模板會被政策閘
// 擋回申請流程（modal 內把那個 403 翻成人看得懂的指路）。
const reissueFor = ref<License | null>(null)

async function onReissued() {
  // 批186 順修：以前是 `store.loadLicenses()` 不帶參數——過濾與籤全掉（下拉還顯示著）、計數也沒刷新。
  await reload()
  await dialogs.notify({
    title: '已換發新證',
    message: '舊的那張已標成「已被取代」。離線客戶請用同一列的「下載授權檔」把新證帶過去。',
  })
}

// --- 取證交付（批108）-------------------------------------------------------
//
// 兩顆按鈕（下載檔案／複製憑證）共用同一支端點——兩種帶走方式都會記到同一筆稽核。
// ★ 代管站的證由中台派送，後端一律 403；按鈕在 UI 上**反灰但不藏**（藏起來會被當成 bug）。
const takeoutBusy = ref<string | null>(null)

/** 這張證能不能人工帶走（代管站不行；非生效中的證後端也會擋）。 */
function canTakeOut(lic: License): boolean {
  return !lic.managed_host_id && effectiveStatus(lic) !== 'revoked' && lic.status === 'active'
}

function takeoutHint(lic: License): string | undefined {
  if (lic.managed_host_id)
    return '本站由中台代管，授權會自動派送到站台——不提供人工取證'
  if (lic.status !== 'active') return '只有生效中的授權可以下載（避免客戶誤裝失效證）'
  return undefined
}

/** 下載授權檔：檔名固定 `license.ipolic`，客戶放進站台的 `license/` 目錄即可生效。 */
async function downloadLicense(lic: License) {
  takeoutBusy.value = lic.license_id
  try {
    const res = await ls.licenseFile(lic.license_id)
    saveTextFile(res.blob, LICENSE_FILENAME)
    await dialogs.notify({
      title: '已下載授權檔',
      message:
        `已存成 ${LICENSE_FILENAME}。交給客戶放到站台的 license/ 目錄後重啟即可生效` +
        '（離線站的標準流程），或請他貼進站台〈系統 → 授權 → 手動安裝 .lic〉。',
    })
  } catch (e) {
    await fail(e, '下載授權檔失敗')
  } finally {
    takeoutBusy.value = null
  }
}

/** 複製憑證原文。★ `copyText` 回 false＝這個環境沒有剪貼簿權限，要把原文交出去。 */
async function copyLicense(lic: License) {
  takeoutBusy.value = lic.license_id
  try {
    const res = await ls.licenseFile(lic.license_id)
    if (await copyText(res.blob)) {
      await dialogs.notify({
        title: '已複製憑證',
        message: '授權原文已複製到剪貼簿，貼進站台〈系統 → 授權 → 手動安裝 .lic〉即可。',
      })
    } else {
      await dialogs.notify({
        title: '無法自動複製',
        message: `這個瀏覽器不允許存取剪貼簿，請手動複製下面這段：\n\n${res.blob}`,
      })
    }
  } catch (e) {
    await fail(e, '取得憑證失敗')
  } finally {
    takeoutBusy.value = null
  }
}

/** 下載單張子憑證（離線站人工貼進產品端）。後端會記稽核。 */
async function saveAddon(a: LicenseAddon) {
  try {
    const res = await ls.downloadAddonBlob(a.addon_id)
    saveTextFile(res.blob, ADDON_FILENAME)
    await dialogs.notify({
      title: '已下載子憑證',
      message: `「${a.label}」已存成 ${ADDON_FILENAME}，貼進站台〈系統 → 授權 → 手動安裝 .lic〉即可。`,
    })
  } catch (e) {
    await fail(e, '下載失敗')
  }
}

/** 複製單張子憑證原文。 */
async function copyAddon(a: LicenseAddon) {
  try {
    const res = await ls.downloadAddonBlob(a.addon_id)
    if (await copyText(res.blob)) {
      await dialogs.notify({
        title: '已複製子憑證',
        message: `「${a.label}」的簽章原文已複製到剪貼簿，貼進站台〈系統 → 授權〉即可。`,
      })
    } else {
      await dialogs.notify({
        title: '無法自動複製',
        message: `這個瀏覽器不允許存取剪貼簿，請手動複製下面這段：\n\n${res.blob}`,
      })
    }
  } catch (e) {
    await fail(e, '取得子憑證失敗')
  }
}

// --- 批次補模組鍵（批81 需求 10／裁示 19）------------------------------------
//
// CS 的 entitlement 語意是**嚴格「鍵缺席＝停用」**（不做 grandfather）。每次產品新增一個
// 模組鍵（批81 的 `api`＝對外 API），所有既有授權都少那個鍵——站台一升版就整片停用，
// 而且錯誤看起來像「我方沒賣」。所以流程固定是：
//   ① 這裡試算（人工核對，特別看有沒有 lite 客戶被順手補上）→ ② 補鍵 →
//   ③ 各站取得新證（自部署等心跳／代管 agent 60 秒／airgapped 人工下載）→ ④ 才升 CS。
const bfOpen = ref(false)
/** 批93：補鍵一律限定在**一個產品**內（模組鍵是產品自己的語彙）。 */
const bfProduct = ref('')
const bfKey = ref('api')
const bfValue = ref('*')
type BfScope = 'all' | 'customer' | 'host'
const bfScope = ref<BfScope>('all')
const bfCustomer = ref('')
const bfHostIid = ref('')
const bfBusy = ref(false)
const bfPreview = ref<ls.BackfillResult | null>(null)

function openBackfill() {
  bfProduct.value =
    productFilter.value ||
    (store.products.find((p) => p.status === 'active') ?? store.products[0])?.key ||
    ''
  bfKey.value = 'api'
  bfValue.value = '*'
  bfScope.value = 'all'
  bfCustomer.value = ''
  bfHostIid.value = ''
  bfPreview.value = null
  bfOpen.value = true
}

/** 範圍三擇一——後端也擋（422 `scope_required`），這裡先擋是為了不讓人白按。 */
function bfBody(dryRun: boolean): ls.BackfillInput | null {
  const key = bfKey.value.trim()
  const val = bfValue.value.trim()
  if (!key || !val) return null
  if (!bfProduct.value) return null
  const body: ls.BackfillInput = {
    product: bfProduct.value,
    modules: { [key]: val },
    dry_run: dryRun,
  }
  if (bfScope.value === 'all') body.all = true
  else if (bfScope.value === 'customer') {
    if (!bfCustomer.value) return null
    body.customer_id = bfCustomer.value
  } else {
    if (!bfHostIid.value.trim()) return null
    body.host_instance_id = bfHostIid.value.trim()
  }
  return body
}

const bfReady = computed(() => bfBody(true) !== null)
/** 試算結果裡的 lite 客戶——輕量級套裝本來就不含對外 API，補了等於白送。 */
const bfLiteCount = computed(() => (bfPreview.value?.rows ?? []).filter((r) => r.plan === 'lite').length)

async function bfDryRun() {
  const body = bfBody(true)
  if (!body) return
  bfBusy.value = true
  bfPreview.value = null
  try {
    bfPreview.value = await ls.backfillModules(body)
  } catch (e) {
    await fail(e, '試算失敗')
  } finally {
    bfBusy.value = false
  }
}

async function bfApply() {
  const body = bfBody(false)
  if (!body || !bfPreview.value) return
  const n = bfPreview.value.to_resign ?? 0
  if (!n) return
  const liteN = bfLiteCount.value
  const liteHint = liteN
    ? `
其中 ${liteN} 張是輕量級（該套裝本不含對外 API），補了等同免費贈送。`
    : ''
  if (
    !(await dialogs.confirm({
      title: '批次補模組鍵',
      message: `將重簽 ${n} 張授權並補上「${bfKey.value.trim()}」。${liteHint}
舊證會標記為「已被取代」，各站需重新取得新證後才會生效。`,
      confirmText: `確認補鍵（${n} 張）`,
    }))
  )
    return
  bfBusy.value = true
  try {
    const res = await ls.backfillModules(body)
    bfOpen.value = false
    await reload()
    await dialogs.notify({
      title: '已補鍵',
      message: `重簽 ${res.resigned?.length ?? 0} 張、略過 ${res.skipped} 張。請確認各站已取得新證後再升級站台版本。`,
    })
  } catch (e) {
    await fail(e, '補鍵失敗')
  } finally {
    bfBusy.value = false
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>授權</h1>
      <!-- 批121：這個數字是**目前這個籤的全表筆數**（伺服器算的），不再是「已載入幾張」。 -->
      <span class="count">{{ counts[filter] }}</span>
      <span class="spacer" />
      <label class="hf">
        <span>客戶</span>
        <select v-model="custFilter">
          <option value="">全部客戶</option>
          <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c) }}</option>
        </select>
      </label>
      <label class="hf">
        <span>代管主機</span>
        <select v-model="hostFilter">
          <option value="">全部主機</option>
          <option v-for="h in hosts" :key="h.id" :value="h.id">{{ h.name }}</option>
        </select>
      </label>
      <!-- 批107：過濾的是**簽發快照**（`licenses.reseller_id`），與列上那顆膠囊同源。 -->
      <label class="hf">
        <span>代理商</span>
        <select v-model="resFilter">
          <option value="">全部</option>
          <option value="none">直營</option>
          <option v-for="r in store.resellers" :key="r.id" :value="r.id">{{ r.name }}</option>
        </select>
      </label>
      <label class="hf">
        <span>產品</span>
        <select v-model="productFilter">
          <option value="">全部產品</option>
          <option v-for="p in store.products" :key="p.key" :value="p.key">{{ p.name }}</option>
        </select>
      </label>
      <FilterClearButton :dirty="filtersDirty" @click="pf.clear()" />
      <UiButton
        v-if="auth.isAdminOrAbove"
        variant="secondary"
        title="產品新增模組鍵時，批次為既有授權補上並重簽（缺鍵＝停用，升版前必做）"
        @click="openBackfill()"
      >
        批次補模組鍵
      </UiButton>
    </div>
    <div class="card">
      <div class="tabs filters" data-hscroll>
        <button
          v-for="f in FILTERS"
          :key="f.key"
          type="button"
          class="tab"
          :class="{ active: filter === f.key }"
          :title="f.hint"
          @click="filter = f.key"
        >
          {{ f.label }}<span class="tab-n">{{ counts[f.key] }}</span>
        </button>
      </div>
      <div v-if="!rows.length" class="empty"><div class="empty-sub">這個分類目前沒有授權。</div></div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table lic-tbl">
          <thead>
            <!--
              6 欄（批110）：原本 9 欄的 min-content 需求超過 1500px，1366 筆電扣掉側欄與
              padding 只剩約 1023px → 表格撐破卡片、資料列跑到白底外面。合併兩組天然成對的
              欄位（產品·主機、狀態·到期），再把模組／套裝／配額收成一句摘要，點開看全部。
            -->
            <tr>
              <th>客戶</th><th>產品・主機</th><th>狀態・到期</th><th>配額</th>
              <th>授權內容</th><th></th>
            </tr>
          </thead>
          <tbody>
            <!-- 一列授權＝兩個 `<tr>`（本體＋加購展開列）→ 必須用 `<template v-for>` 包住，
                 否則第二個 `<tr>` 在 v-for 作用域外（拿不到 `r`）。 -->
            <template v-for="r in rows" :key="r.lic.license_id">
            <tr>
              <td>
                <div class="hostcell">
                  <span>{{ store.customerName(r.lic.customer_id) }}</span>
                  <!-- 批93-C：經手代理商（簽發當下的快照，不隨客戶改派而變）。直營不畫，
                       沒有標就是我方自己賣的——多一行「直營」只是把每一列都加高。 -->
                  <span v-if="r.lic.reseller_id" class="chip rs" :title="'經手代理商（簽發當下）'">
                    {{ r.lic.reseller_name ?? store.resellerName(r.lic.reseller_id) }}
                  </span>
                  <code class="mono" :title="r.lic.license_id">{{ r.lic.license_id.slice(0, 8) }}</code>
                </div>
              </td>
              <td class="prod">
                <div class="hostcell">
                  <span class="prod-name">{{ store.productName(r.lic.product) }}</span>
                  <span class="sub">{{ r.lic.instance_name || '—' }}</span>
                  <code v-if="r.lic.host_instance_id" class="mono" :title="r.lic.host_instance_id">{{ r.lic.host_instance_id.slice(0, 12) }}</code>
                  <!--
                    代管維度（批80 批B3）——**不加新欄**（表已 8 欄、配額 chip 要空間），
                    以第三行 chip 呈現。自部署不顯示（沒有 chip 就是自部署）。
                  -->
                  <RouterLink
                    v-if="r.lic.managed_host_id"
                    class="chip mh"
                    :to="{ name: 'host-detail', params: { id: r.lic.managed_host_id } }"
                    title="這張授權綁的站台在我方代管的 VM 上（點擊看主機詳情）"
                  >
                    代管 {{ r.lic.managed_host_name ?? r.lic.managed_host_id.slice(0, 8) }}
                  </RouterLink>
                </div>
              </td>
              <td class="status" :class="{ warn: r.expiringSoon }">
                <span class="status-pill" :class="r.status">{{ statusLabel(r.status) }}</span>
                <!-- 批93-C break-glass 標記：`direct`＝superadmin 越過核發政策直接簽的。
                     批104 再加一種 `redeem`＝客戶使用兌換碼簽出的證。
                     仍然**只標這兩種**——`request`／`policy`／`system` 都是正常銷售路徑，
                     全部標等於沒標。中文名走 `lib/licenseLabels.ts` 的對照表。 -->
                <span
                  v-if="r.lic.issued_via === 'direct' || r.lic.issued_via === 'redeem'"
                  class="chip"
                  :class="r.lic.issued_via"
                  :title="
                    r.lic.issued_via === 'direct'
                      ? `未經申請流程、由${ROLE_LABELS.superadmin}直接簽發（已記稽核並發告警）`
                      : issuedViaHint(r.lic.issued_via)
                  "
                >
                  {{ issuedViaLabel(r.lic.issued_via) }}
                </span>
                <!-- 批110：延後生效的證（LS #5496）。站台在那之前繼續用手上那張。 -->
                <span
                  v-if="notYet(r.lic)"
                  class="chip pending"
                  :title="`${fmt(r.lic.not_before ?? null)} 起生效`"
                >尚未生效</span>
                <!-- 批110：到期併進本欄（原本是第 8 欄）。 -->
                <div class="exp-line" :class="{ warn: r.expiringSoon }">
                  {{ fmt(r.lic.expires_at) }}
                  <!-- 批108：期限可以短到分鐘，一律印天數會讓短期憑證都顯示「剩 0 天」。 -->
                  <small v-if="formatRemaining(r.lic.expires_at)">
                    （{{ formatRemaining(r.lic.expires_at) }}）
                  </small>
                </div>
                <div class="sub">
                  {{ modeLabel(r.lic.deployment_mode) }}
                  <span
                    v-if="isShortTerm(r.lic.term_days, r.lic.term_minutes)"
                    class="chip short-term"
                    title="這是短期（不足一天）的臨時憑證——到期後站台會轉唯讀，記得補發正式授權"
                  >短期</span>
                </div>
              </td>
              <!--
                批114：原本是三行硬編的「某產品的三個數字欄」。配額鍵由該產品的憑證設計
                宣告（數量、名稱、用量來源全都不一樣），所以改成前幾項＋`+N` 的摘要，
                滑鼠移上去看全部（`<QuotaSummary>`）。有用量時顯示「已用／上限」，
                超量或接近上限的鍵會排到最前面並變色。
              -->
              <td class="quota">
                <QuotaSummary
                  :spec="store.productByKey(r.lic.product) ?? null"
                  :quotas="r.quotas"
                  :usage="r.lic.usage ?? null"
                />
              </td>
              <!--
                批110：模組／套裝／配額／子證收成一句摘要，點開〈授權內容〉看全部。
                留在表格裡的是「一眼要判斷的事」：額度有沒有超用、有沒有加購、回報新不新。
              -->
              <td class="content">
                <button type="button" class="content-btn" @click="openDetail(r.lic)">
                  <span class="plan">{{ planLabel(store.productByKey(r.lic.product), r.lic.plan) }}</span>
                  <span class="sum">
                    模組 {{ r.modules.length }}・額度 {{ r.quotaRows.length }}<template
                      v-if="r.lic.addon_count"
                    >・子證 {{ r.lic.addon_count }}</template>
                  </span>
                  <span v-if="r.quotaRows.some((q) => q.over)" class="chip over">額度已滿</span>
                  <span v-if="r.awaitingReport" class="subtle">（尚未回報）</span>
                </button>
                <div v-if="r.lic.usage" class="small seen" :class="staleLevel(r.lic.usage.last_seen)">
                  回報 {{ fmtSeen(r.lic.usage.last_seen, { short: true }) }}
                </div>
              </td>
              <td class="acts-cell">
                <div class="acts">
                  <template v-if="renewingId === r.lic.license_id">
                    <input v-model="renewDate" type="date" class="sm" />
                    <UiButton variant="primary" size="sm" @click="renew(r.lic)">確定</UiButton>
                    <UiButton size="sm" @click="renewingId = null">取消</UiButton>
                  </template>
                  <template v-else-if="r.lic.status === 'active'">
                    <!-- 批108：取證交付。代管站**反灰但不藏**——藏起來會被當成 bug。 -->
                    <UiButton
                      variant="primary"
                      size="sm"
                      :disabled="!canTakeOut(r.lic)"
                      :loading="takeoutBusy === r.lic.license_id"
                      :title="takeoutHint(r.lic)"
                      @click="downloadLicense(r.lic)"
                    >下載授權檔</UiButton>
                    <!-- ★ 列內的次要動作一律 `secondary`（有框），不可留空讓它 fallback 成
                         `ghost`——ui.css 批81 那段註解寫得很清楚：「ghost 是無框無底的
                         『弱到看不出是按鈕』，一整排並列時分不出誰可按」。這一列是
                         primary（下載）＋ secondary ×3 ＋ danger（撤銷）三級，各有語意。 -->
                    <UiButton
                      variant="secondary"
                      size="sm"
                      :disabled="!canTakeOut(r.lic)"
                      :title="takeoutHint(r.lic)"
                      @click="copyLicense(r.lic)"
                    >複製憑證</UiButton>
                    <!-- 批113：**未綁主機的證換發不了**（後端 422 `base_has_no_host`
                         ——換發是以機器為錨點重簽）。按了才知道等於白按一次，反灰＋
                         指路到客戶頁直接簽發。 -->
                    <UiButton
                      variant="secondary"
                      size="sm"
                      :disabled="!r.lic.host_instance_id"
                      :title="
                        r.lic.host_instance_id
                          ? undefined
                          : '未綁主機的授權無法換發，請用〈客戶〉頁直接簽發'
                      "
                      @click="reissueFor = r.lic"
                    >換發新證</UiButton>
                    <UiButton variant="secondary" size="sm" @click="startRenew(r.lic)">續約</UiButton>
                    <UiButton variant="danger" size="sm" @click="revoke(r.lic)">撤銷</UiButton>
                  </template>
                  <template v-else>
                    <UiButton variant="danger" size="sm" @click="remove(r.lic)">刪除</UiButton>
                  </template>
                </div>
              </td>
            </tr>
            </template>
          </tbody>
        </table>
      </div>
      <!-- keyset 分頁：後端一頁 50 筆。`next_cursor` 非 null 才畫這一列。
           ★ 批121：分母講清楚是「這個籤有幾張」——以前只印「已載入 50 張」，
             跟畫面上的 2 列並排時，沒人看得出 50 是什麼。 -->
      <div v-if="store.licensesNextCursor" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <span class="subtle">已顯示 {{ store.licenses.length }} / {{ counts[filter] }} 張</span>
      </div>
    </div>

    <!-- 批次補模組鍵（批81）：試算 → 人工核對 → 二次確認才動手 -->
    <!-- 批108：換發新證（以機器為錨點重簽，可同時改套裝／配額／期限） -->
    <LicenseDetailModal
      :open="!!detailFor"
      :license="detailFor"
      :spec="detailFor ? (store.productByKey(detailFor.product) ?? null) : null"
      :addons="addonsOpen === detailFor?.license_id ? addonRows : []"
      :addons-busy="addonsBusy"
      :can-manage-addons="auth.isManagerOrAbove"
      :can-reissue="detailFor?.status === 'active'"
      @close="detailFor = null"
      @reissue="
        () => {
          reissueFor = detailFor
          detailFor = null
        }
      "
      @addon-save="saveAddon"
      @addon-copy="copyAddon"
      @addon-renew="(a) => detailFor && renewAddon(detailFor, a)"
      @addon-revoke="(a) => detailFor && revokeAddon(detailFor, a)"
    />

    <ReissueModal
      :open="reissueFor !== null"
      :license="reissueFor"
      :spec="reissueFor ? (store.productByKey(reissueFor.product) ?? null) : null"
      :allow-internal="auth.isManagerOrAbove"
      @close="reissueFor = null"
      @done="onReissued"
    />

    <UiModal :open="bfOpen" title="批次補模組鍵" width="900px" @close="bfOpen = false">
      <p class="bf-note">
        為既有的<strong>生效中</strong>授權補上缺少的模組鍵並重簽（舊證標記「已被取代」）。
        產品端的語意是<strong>「鍵缺席＝停用」</strong>，所以新增模組後
        <strong>務必先補鍵、確認各站取得新證，再升級站台版本</strong>。
        派送不需要另外派工：自部署站走心跳（最長 75 分，或請客戶按「取回已核發」）、
        代管站由 agent 60 秒內套用、離線（氣隙）站只能人工下載。
      </p>

      <div class="bf-form">
        <label class="bf-f">
          <!-- 批93：**必選**。`all=true` 的語意是「**該產品的**全部授權」——不帶產品會
               掃到別的產品的證，把不屬於它的模組鍵補上去。 -->
          <span>產品</span>
          <select v-model="bfProduct">
            <option v-for="p in store.products" :key="p.key" :value="p.key">{{ p.name }}</option>
          </select>
        </label>
        <label class="bf-f">
          <span>模組鍵</span>
          <input v-model="bfKey" placeholder="api" />
        </label>
        <label class="bf-f">
          <span>範圍值</span>
          <input v-model="bfValue" placeholder="*" />
        </label>
      </div>

      <fieldset class="bf-scope">
        <legend>套用範圍</legend>
        <label><input v-model="bfScope" type="radio" value="all" /> 全部客戶</label>
        <label><input v-model="bfScope" type="radio" value="customer" /> 指定客戶</label>
        <label><input v-model="bfScope" type="radio" value="host" /> 指定主機</label>
        <select v-if="bfScope === 'customer'" v-model="bfCustomer">
          <option value="">請選擇客戶…</option>
          <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c) }}</option>
        </select>
        <input
          v-if="bfScope === 'host'"
          v-model="bfHostIid"
          class="bf-iid"
          placeholder="instance_id（主機識別碼）"
        />
      </fieldset>

      <div v-if="bfPreview" class="bf-result">
        <p class="bf-sum">
          將重簽 <strong>{{ bfPreview.to_resign ?? 0 }}</strong> 張，
          略過 <strong>{{ bfPreview.skipped }}</strong> 張（已有這個鍵）。
        </p>
        <p v-if="bfLiteCount" class="bf-warn">
          ⚠️ 其中 {{ bfLiteCount }} 張是<strong>輕量級</strong>——該套裝本來就不含對外 API，
          補上等同免費贈送，請先確認是否要縮小範圍。
        </p>
        <div v-if="!(bfPreview.rows ?? []).length" class="bf-empty">沒有需要補鍵的授權。</div>
        <div v-else class="bf-tbl-wrap">
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table">
              <thead>
                <tr>
                  <th>客戶</th><th>主機</th><th>套裝</th><th>型態</th>
                  <th>到期</th><th>補上</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in bfPreview.rows" :key="row.license_id" :class="{ lite: row.plan === 'lite' }">
                  <td>
                    {{ row.customer_name ?? '—' }}
                    <code class="mono">{{ row.license_id.slice(0, 8) }}</code>
                  </td>
                  <td>{{ row.instance_name || row.host_instance_id?.slice(0, 12) || '—' }}</td>
                  <td>{{ planLabel(store.productByKey(bfProduct), row.plan) }}<span v-if="row.trial" class="chip">試用</span></td>
                  <td>
                    <span v-if="row.hosted" class="chip">代管</span>
                    <span v-else class="chip">自部署</span>
                    <span v-if="row.deployment_mode !== 'online'" class="chip">{{ modeLabel(row.deployment_mode) }}</span>
                  </td>
                  <td>{{ row.expires_at ? fmt(row.expires_at) : `未啟用（${row.term_days ?? '?'} 天）` }}</td>
                  <td>{{ row.missing.join('、') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <template #foot>
        <UiButton :disabled="bfBusy" @click="bfOpen = false">關閉</UiButton>
        <UiButton :disabled="bfBusy || !bfReady" @click="bfDryRun()">試算</UiButton>
        <UiButton
          variant="primary"
          :disabled="bfBusy || !bfPreview || !(bfPreview.to_resign ?? 0)"
          @click="bfApply()"
        >
          確認補鍵（{{ bfPreview?.to_resign ?? 0 }} 張）
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
/* 批108：授權列的動作從「續約／撤銷」兩顆變成四顆（＋下載授權檔／複製憑證）。
   共用樣式 `.tbl .acts` 是 `nowrap`（其他頁按鈕少，不折行比較整齊），這裡只覆寫本頁
   ——不然窄視窗下整個表格會被這一欄撐到橫向捲動。 */
.tbl .acts {
  flex-wrap: wrap;
  justify-content: flex-end;
}
.tbl td.acts-cell {
  white-space: normal;
  min-width: 250px;
}
/* 平板（批171 驗收追加）：操作欄釘在右側時會蓋住〈授權內容〉——收窄成兩行按鈕（約 190px），
   讓出寬度給被蓋住的欄位。手機是卡片（ui.css 的卡片規則較具體，不受影響）。 */
@media (--compact) {
  .tbl td.acts-cell {
    min-width: 0;
  }
  .tbl .acts {
    width: max-content;
    max-width: 190px;
    margin-left: auto;
  }
}

/* 批108：短期（不足一天）的臨時憑證——救援／教育訓練用，到期後站台會轉唯讀，
   一眼看得出「這台還欠一張正式證」。 */
.chip.short-term {
  margin-top: 4px;
  border-color: var(--warning, var(--border-1));
  color: var(--warning, var(--fg-2));
}

/* 批93-B：被加購提升的模組／配額 chip 的小標 */
.chip.has-addon {
  border-color: var(--brand-600, var(--border-1));
}
.ad-tag {
  margin-left: 4px;
  color: var(--brand-600, var(--fg-3));
  font-size: var(--fs-10, 10px);
  font-weight: 600;
}

/* 批93-B：加購子憑證（展開列） */
.addon-toggle {
  margin-top: 4px;
  cursor: pointer;
  border: 1px solid var(--border-1);
  background: transparent;
  color: var(--fg-3);
}
.addon-toggle.on {
  color: var(--brand-600, var(--fg-1));
  border-color: var(--brand-600, var(--border-1));
  font-weight: 600;
}
.addon-row > td {
  background: var(--bg-2, transparent);
}
.addon-tbl {
  width: 100%;
}
.addon-offline {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-11);
  color: var(--warning);
}
/* 批93：產品欄——名稱不折行（「愛+1 社群工作台」被折成三行會把整列撐高）。
   批110 起這一欄同時裝主機（第二行）與代管 chip（第三行）。 */
.prod {
  white-space: nowrap;
}
.prod-name {
  font-weight: 600;
}
/* --- 批次補模組鍵 modal（批81）--- */
.bf-note {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-12);
  color: var(--fg-3);
  line-height: 1.7;
}
.bf-form {
  display: flex;
  gap: var(--sp-3);
  flex-wrap: wrap;
  margin-bottom: var(--sp-3);
}
.bf-f {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.bf-f input {
  width: 160px;
}
.bf-scope {
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  padding: 6px 14px 10px;
  margin: 0 0 var(--sp-3);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
}
.bf-scope legend {
  padding: 0 8px;
  font-size: var(--fs-12);
  color: var(--fg-3);
  font-weight: 600;
}
.bf-scope label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-12);
  color: var(--fg-2);
  cursor: pointer;
}
.bf-iid {
  min-width: 280px;
}
.bf-sum {
  margin: 0 0 4px;
  font-size: var(--fs-13);
  color: var(--fg);
}
.bf-warn {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-12);
  color: var(--warning);
}
.bf-empty {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
/* 列多時內部捲動，別把整個 modal 撐爆 */
.bf-tbl-wrap {
  max-height: 46vh;
  overflow: auto;
}
/* 輕量級列標紅：該套裝本不含對外 API，補了是白送 */
.bf-result tbody tr.lite td {
  color: var(--danger);
}
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
  flex-wrap: wrap;
}
/* keyset 分頁的「載入更多」列（形狀與 ResellerLicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.filters {
  margin-bottom: var(--sp-3);
}
.tab-n {
  margin-left: 6px;
  font-size: var(--fs-11);
  font-weight: 500;
  color: var(--fg-4);
}
.tab.active .tab-n {
  color: inherit;
}
/* 十項配額 chip 在欄內換行、不橫向捲動（主人裁示）：只給最小寬，其餘交給 auto layout */
.lic-tbl {
  table-layout: auto;
}
.hostcell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.hostcell code {
  white-space: nowrap;
}
/* 批110：`.status` 現在裝「狀態 pill ＋ 直發／兌換 chip ＋ 尚未生效 ＋ 到期」四種東西，
   **不可以** nowrap——硬擠一行正是 1366px 下把這一欄撐到 200px 以上的主因。 */
.status {
  white-space: normal;
}
/* 批114：配額摘要欄。`<QuotaSummary>` 自己 nowrap＋省略號，這裡只負責不讓欄變太寬。 */
.quota {
  max-width: 180px;
}
.sub,
.small {
  font-size: var(--fs-11);
  margin-top: 2px;
}
.sub {
  color: var(--fg-3);
}
/* 到期臨近的色階（與後端 license_expiring 告警同一批授權）。 */
.sub.warn {
  color: var(--warning);
  font-weight: 600;
}
/* 代管 chip：可點（RouterLink），但形狀維持 chip 不是連結色。 */
.chip.mh {
  text-decoration: none;
  color: var(--fg-2);
}
.chip.mh:hover {
  background: var(--bg-hover);
  text-decoration: underline;
}
/* 批93-C：經手代理商（中性小標，與代管 chip 同一階）＋ 直發（break-glass，要顯眼） */
.chip.rs {
  font-size: var(--fs-11);
  color: var(--fg-3);
}
.chip.direct {
  margin-left: 4px;
  font-size: var(--fs-10, 10px);
  color: var(--warning);
  border-color: var(--warning);
  cursor: help;
}
/* 批104：兌換（中性品牌色——它是正常的業務活動，不是 break-glass）。 */
.chip.redeem {
  margin-left: 4px;
  font-size: var(--fs-10, 10px);
  color: var(--brand-600);
  border-color: var(--brand-400);
  cursor: help;
}
/* 批110：模組與配額的 chip 牆整個搬進〈授權內容〉詳情 modal，清單只留一句摘要。
   原本 `.mods { min-width: 160px }` ＋ `.quota { min-width: 240px }` 這兩個下限，
   光是它們就吃掉 1366px 可用寬度的四成。 */
.content {
  min-width: 180px;
}
.content-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  border: 1px solid var(--border-2);
  border-radius: var(--r-sm);
  background: none;
  padding: 4px 8px;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.content-btn:hover {
  border-color: var(--border-strong);
  background: var(--bg-hover);
}
.content-btn .sum {
  color: var(--fg-3);
  font-size: var(--fs-11);
}
/* 到期行（併進狀態欄之後自成一行，與 pill 拉開一點距離）。 */
.exp-line {
  margin-top: 2px;
  font-size: var(--fs-11);
  color: var(--fg-2);
}
.exp-line.warn {
  color: var(--warning);
  font-weight: 600;
}
.plan {
  font-weight: 600;
  margin-bottom: 4px;
}
.plan .subtle {
  font-weight: 400;
}
</style>
